import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { useCreateReimbursementRequest } from "@/hooks/mutations/use-create-reimbursement-request";
import { DocumentUploader } from "../document-uploader";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";

const ReimbursementSchema = z.object({
	requester_name: z.string().nonempty("Requester name is required"),
	requester_type: z.enum(["internal", "external"]),
	requester_notes: z.string().optional(),
	amount: z.number().positive("Amount must be greater than 0"),
	status: z.enum([
		"PENDING",
		"REQUESTED",
		"IN_REVIEW",
		"CHANGES_REQUESTED",
		"COMPLETE",
		"DENIED",
	]),
});

type ReimbursementFormType = z.infer<typeof ReimbursementSchema>;

export function CreateReimbursementForm({
	onComplete,
}: {
	onComplete: () => void;
}) {
	const client = useQueryClient();
	const { mutate: createReimbursement, isPending } =
		useCreateReimbursementRequest();
	const [requesterType, setRequesterType] = useState<"internal" | "external">(
		"internal",
	);
	const [status, setStatus] = useState<
		| "PENDING"
		| "REQUESTED"
		| "IN_REVIEW"
		| "CHANGES_REQUESTED"
		| "COMPLETE"
		| "DENIED"
	>("PENDING");
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
	} = useForm<ReimbursementFormType>({
		defaultValues: {
			requester_name: "",
			requester_type: "internal",
			requester_notes: "",
			amount: 0,
			status: "PENDING",
		},
		resolver: zodResolver(ReimbursementSchema),
		reValidateMode: "onSubmit",
	});

	const onSubmit = (data: ReimbursementFormType) => {
		// Create FormData to support file uploads
		const formData = new FormData();

		// Add regular fields
		formData.append("requester_name", data.requester_name);
		formData.append("requester_type", data.requester_type);
		formData.append("amount", data.amount.toString());
		formData.append("status", data.status);

		// Add optional notes if provided
		if (data.requester_notes) {
			formData.append("requester_notes", data.requester_notes);
		}

		// Add files if any were uploaded
		// PocketBase expects multiple files with the same field name
		for (const file of uploadedFiles) {
			formData.append("documents", file);
		}

		createReimbursement(formData, {
			onError: (err) => {
				toast.error(err.message || "Failed to create reimbursement request");
			},
			onSuccess: () => {
				toast.success("Reimbursement request created");
				client.invalidateQueries({ queryKey: ["reimbursements"] });
				onComplete();
			},
		});
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<Label>Requester Name</Label>
				<Input {...register("requester_name")} placeholder="Your name" />
				{errors.requester_name && (
					<p className="text-xs text-red-600">
						{errors.requester_name.message}
					</p>
				)}
			</div>

			<div className="flex flex-col gap-2">
				<Label>Requester Type</Label>
				<Select
					value={requesterType}
					onValueChange={(value) => {
						const type = value as "internal" | "external";
						setRequesterType(type);
						setValue("requester_type", type);
					}}
				>
					<SelectTrigger className="w-full">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="internal">Internal</SelectItem>
						<SelectItem value="external">External</SelectItem>
					</SelectContent>
				</Select>
				{errors.requester_type && (
					<p className="text-xs text-red-600">
						{errors.requester_type.message}
					</p>
				)}
			</div>

			<div className="flex flex-col gap-2">
				<Label>Amount ($)</Label>
				<Input
					{...register("amount", { valueAsNumber: true })}
					type="number"
					step="0.01"
					min="0"
					placeholder="0.00"
				/>
				{errors.amount && (
					<p className="text-xs text-red-600">{errors.amount.message}</p>
				)}
			</div>

			<div className="flex flex-col gap-2">
				<Label>Status</Label>
				<Select
					value={status}
					onValueChange={(value) => {
						const statusValue = value as
							| "PENDING"
							| "REQUESTED"
							| "IN_REVIEW"
							| "CHANGES_REQUESTED"
							| "COMPLETE"
							| "DENIED";
						setStatus(statusValue);
						setValue("status", statusValue);
					}}
				>
					<SelectTrigger className="w-full">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="PENDING">Pending</SelectItem>
						<SelectItem value="REQUESTED">Requested</SelectItem>
						<SelectItem value="IN_REVIEW">In Review</SelectItem>
						<SelectItem value="CHANGES_REQUESTED">Changes Requested</SelectItem>
						<SelectItem value="COMPLETE">Complete</SelectItem>
						<SelectItem value="DENIED">Denied</SelectItem>
					</SelectContent>
				</Select>
				{errors.status && (
					<p className="text-xs text-red-600">{errors.status.message}</p>
				)}
			</div>

			<div className="flex flex-col gap-2">
				<Label>Notes (Optional)</Label>
				<Textarea
					{...register("requester_notes")}
					placeholder="Add any additional notes or details about this reimbursement request..."
					rows={4}
				/>
				{errors.requester_notes && (
					<p className="text-xs text-red-600">
						{errors.requester_notes.message}
					</p>
				)}
			</div>

			<div className="flex flex-col gap-2">
				<Label>Documents (Optional)</Label>
				<DocumentUploader
					maxFiles={5}
					maxFileSize={10 * 1024 * 1024} // 10MB
					onFilesChange={setUploadedFiles}
				/>
				{uploadedFiles.length > 0 && (
					<p className="text-xs text-muted-foreground">
						{uploadedFiles.length} file{uploadedFiles.length !== 1 ? "s" : ""}{" "}
						selected
					</p>
				)}
			</div>

			<Button type="submit" disabled={isPending}>
				{isPending ? "Submitting..." : "Submit Reimbursement Request"}
			</Button>
		</form>
	);
}
